import { adsAssistantClientFetch } from "./adsAssistantClientHttp";

const SUGGESTION_STATS_BASE = "/api/ads-assistant/suggestion-stats";
const ACTIVITY_BASE = "/api/ads-assistant/activity";
const SALE_CLAIMS_BASE = "/api/ads-assistant/sale-claims";
const GENERATE_SIMILAR_BASE = "/api/ads-assistant/generate-similar";
const INGEST_TEMPLATEIZE_BASE = "/api/ads-assistant/ingest-templateize";
const ARCHIVE_RETRIEVAL_BASE = "/api/ads-assistant/archive-retrieval";

export type AdsSuggestionStatsResponse = {
  ok: true;
  scope: string;
  feed_retention_days: number;
  published_at_min: string;
  org_total_posts: number;
  my_total_posts: number;
  uncategorized: { org_count: number; my_count: number };
  templates: {
    template_id: string;
    name: string;
    org_count: number;
    my_count: number;
    org_share_pct: number;
    my_share_pct: number;
    first_used_at: string | null;
  }[];
  fresh_templates: AdsSuggestionStatsResponse["templates"];
  gap_suggestions: AdsSuggestionStatsResponse["templates"];
  /** Present on server v2+; treat as empty when missing (older deploys). */
  variety_suggestions?: { template_id: string; name: string; reason_code: "same_template_streak" }[];
  notice?: string;
};

export async function fetchAdsSuggestionStats(scope: "org" | "location") {
  const q = `?scope=${encodeURIComponent(scope)}`;
  return adsAssistantClientFetch<AdsSuggestionStatsResponse>(`${SUGGESTION_STATS_BASE}${q}`, { method: "GET" });
}

/** Server-side status verdict (mirrors `server/admin-handlers/adsActivityVerdict.ts`). */
export type AdsActivityStatusVerdict = {
  tone: "amber" | "green" | "gold" | "red" | "grey";
  label: string;
  message: string;
  next_action_label: string;
  next_action_route: string;
};

export type AdsActivityFreshAchievement = {
  id: string;
  user_id: string;
  organization_id: string;
  achievement_key: string;
  unlocked_at: string;
  metadata: Record<string, unknown>;
  revoked_at?: string | null;
};

export type AdsActivityResponse = {
  ok: true;
  posts_today_count: number;
  posts_in_feed_window_count: number;
  feed_retention_days: number;
  show_recent_logger_names: boolean;
  recent_loggers: { user_id: string; display_name: string; published_at: string }[];
  /** Gamification Phase 1: per-user counts + coaching context. Older deploys may omit these. */
  my_posts_today?: number;
  my_rolling_avg_per_day?: number;
  streak_days?: number;
  ads_daily_goal?: number;
  daily_max_per_platform_total?: number;
  red_cutoff_local_time?: string;
  use_in_org_activity?: boolean;
  org_active_rep_count?: number;
  org_avg_per_rep_today?: number;
  status_verdict?: AdsActivityStatusVerdict | null;
  fresh_achievements?: AdsActivityFreshAchievement[];
};

export async function fetchAdsActivity(params: {
  logged_after: string;
  logged_before: string;
  local_calendar_day: string;
}) {
  const sp = new URLSearchParams();
  sp.set("logged_after", params.logged_after);
  sp.set("logged_before", params.logged_before);
  sp.set("local_calendar_day", params.local_calendar_day);
  return adsAssistantClientFetch<AdsActivityResponse>(`${ACTIVITY_BASE}?${sp}`, { method: "GET" });
}

export type AdsSaleClaimApiBody = {
  ok: true;
  claim: { id: string; claimed_at: string; sale_units?: number } | null;
  celebration: { display_name: string; template_id: string | null; template_name: string | null };
  fresh_achievements?: AdsActivityFreshAchievement[];
};

export async function postAdsSaleClaim(body: { social_post_id: string; note?: string; sale_units?: number }) {
  return adsAssistantClientFetch<AdsSaleClaimApiBody>(SALE_CLAIMS_BASE, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export type AdsGenerateSimilarApiBody = {
  ok: true;
  template_id: string;
  template_name: string;
  slot_fills: Record<string, string>;
  full_post: string;
};

export async function postAdsGenerateSimilar(body: { template_id: string; social_post_id?: string }) {
  return adsAssistantClientFetch<AdsGenerateSimilarApiBody>(GENERATE_SIMILAR_BASE, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export type AdsIngestTemplateizeApiBody = {
  ok: true;
  template_id: string | null;
  template_name: string | null;
  slot_fills: Record<string, string>;
  notes: string;
};

export async function postAdsIngestTemplateize(body: {
  ad_copy: string;
  creative_description?: string;
  hint_template_id?: string;
}) {
  return adsAssistantClientFetch<AdsIngestTemplateizeApiBody>(INGEST_TEMPLATEIZE_BASE, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export type AdsArchiveListApiBody = {
  ok: true;
  archive_enabled: boolean;
  prefix?: string;
  files: { name: string; updated_at: string | null; created_at: string | null }[];
  message?: string;
};

export async function fetchAdsArchiveList() {
  return adsAssistantClientFetch<AdsArchiveListApiBody>(`${ARCHIVE_RETRIEVAL_BASE}?mode=list`, { method: "GET" });
}

export async function fetchAdsArchiveSignedUrl(name: string) {
  const q = `?mode=sign&name=${encodeURIComponent(name)}`;
  return adsAssistantClientFetch<{ ok: true; url: string; expires_in: number }>(`${ARCHIVE_RETRIEVAL_BASE}${q}`, {
    method: "GET",
  });
}
